import * as Collapsible from '@radix-ui/react-collapsible'
import { Box, Button, Flex, Heading, Text } from '@radix-ui/themes'
import { Link } from '@remix-run/react'
import cn from 'clsx'
import parse from 'html-react-parser'
import * as React from 'react'
import { useTranslation } from 'react-i18next'

import { type IconProps } from '#app/components/icon'
import { type Locale } from '#app/models/locale'
import { INDEX_I18NEXT } from '#app/routes/($lang)._index/route-config'

/* -------------------------------------------------------------------------------------------------
 * InfoBlock
 * -----------------------------------------------------------------------------------------------*/

type BlockProps = {
	Icon: React.FC<IconProps>
	title: string
	text: string
	path: string
	locale: Locale
	idx: number
}

const InfoBlock: React.FC<BlockProps> = ({
	Icon,
	idx,
	title,
	text,
	path,
	locale,
}) => {
	const [animate, setAnimate] = React.useState(false)
	const { t } = useTranslation(INDEX_I18NEXT.routeIndex)
	const ref = React.useRef<HTMLAnchorElement | null>(null)
	const [open, setOpen] = React.useState(false)
	const [width, setWidth] = React.useState<number>(0)

	const max = 100
	const widthBreak = 480

	const parsedClosed = React.useMemo(
		() =>
			parse(
				text?.slice(0, max).slice(0, text.slice(0, max).lastIndexOf(' ')) +
					'...' || '',
			),
		[text, max],
	)
	const parsedOpened = React.useMemo(() => parse(text) ?? '', [text])

	React.useEffect(() => {
		const scrollHandler = () => {
			if (
				window.innerHeight / 2 + 30 >
					(ref.current?.getBoundingClientRect().top || 0) &&
				window.innerHeight / 2 - 30 <
					(ref.current?.getBoundingClientRect().top || 0) &&
				width < widthBreak
			) {
				setAnimate(true)
			} else {
				setAnimate(false)
			}
		}
		const resizeHandler = () => {
			setWidth(window.innerWidth)
			setOpen(width >= widthBreak)
		}

		resizeHandler()
		window.addEventListener('scroll', scrollHandler)
		window.addEventListener('resize', resizeHandler)

		return () => {
			window.removeEventListener('scroll', scrollHandler)
			window.removeEventListener('resize', resizeHandler)
		}
	}, [width])

	return (
		<Flex
			direction="column"
			position="relative"
			p="8"
			className={cn(
				'info-box overflow-hidden rounded-md bg-gray-1 shadow',
				'transition duration-500 ease-in-out',
				'hover:bg-accent-1 hover:shadow-md hover:shadow-accent-4',
				{
					'mt-20 md:col-span-2': idx % 5 === 0 && idx,
					'mt-8 md:col-span-2': idx % 5 === 0 && !idx,
				},
			)}
			onMouseEnter={React.useCallback(() => setAnimate(true), [])}
			onMouseLeave={React.useCallback(() => setAnimate(false), [])}
		>
			<Collapsible.Root
				asChild
				open={open}
				onOpenChange={setOpen}
				className="app-Collapsible"
			>
				<Box>
					<Flex direction="column" justify="center" align="center" gapY="3">
						<Flex
							asChild
							direction="column"
							justify="center"
							align="center"
							gapY="1"
						>
							<Link
								ref={ref}
								to={`/${locale}${path}`}
								className="!no-underline"
							>
								<Icon
									width={60}
									height={60}
									className="fill-transparent stroke-accent-8 stroke-12 transition-all duration-700 ease-in-out"
									animate={animate}
								/>
								<Heading as="h3" color="gray">
									{title}
								</Heading>
							</Link>
						</Flex>
						{!open && width < widthBreak && (
							<Text
								as="p"
								color="gray"
								className="mt-2 text-justify leading-relaxed tracking-wide"
							>
								{parsedClosed}
							</Text>
						)}
					</Flex>
					<Collapsible.Content className="app-CollapsibleContent my-3">
						<Text
							asChild
							color="gray"
							className="mt-2 text-justify leading-relaxed tracking-wide"
						>
							<Box>{parsedOpened}</Box>
						</Text>
					</Collapsible.Content>
					{width < widthBreak && (
						<Collapsible.Trigger asChild>
							<Flex justify="center" width="100%" mt="4">
								<Button color="green" size="1" variant="soft">
									{open ? t('button.less') : t('button.more')}
								</Button>
							</Flex>
						</Collapsible.Trigger>
					)}
				</Box>
			</Collapsible.Root>
		</Flex>
	)
}
InfoBlock.displayName = 'InfoBlock'

/* -----------------------------------------------------------------------------------------------*/

export { InfoBlock, type BlockProps }
